<i18n lang="yaml" locale="en">
  email: E-Mail
  projects: Projects
  devices: Devices
  storage: Storage
  user: User
  creator: Creater
  actions: Actions
  add user: Add user.
  couldnt delete user: Could not delete user.
  attention: Attention!
  confirm delete user: Do you really want to delete this user?
  enter new email: Please enter the new user email.
  password: Password
  enter new password: Please enter the new user password.
  enter password: Please enter the user password.
  enter email: Please enter the user e-mail.
  user limit reached: Maximum number of users reached.
  user exists: A user with this e-mail already exists.
</i18n>

<i18n lang="yaml" locale="de">
  email: E-Mail
  projects: Projekte
  devices: Geräte
  storage: Speicher
  user: Nutzer
  creator: Ersteller
  actions: Aktionen
  add user: Nutzer hinzufügen.
  couldnt delete user: Der Benutzer konnte nicht gelöscht werden.
  attention: Achtung!
  confirm delete user: Wollen Sie diesen Nutzer wirklich löschen?
  enter new email: Bitte geben Sie die neue E-Mail-Adresse des Nutzers an.
  password: Passwort
  enter new password: Bitte geben Sie das neue Passwort des Nutzers an.
  enter password: Bitte geben sie das Passwort des Nutzers an.
  enter email: Bitte geben Sie die E-Mail-Adresse des Nutzers an.
  user limit reached: Maximale Anzahl an Nutzern erreicht.
  user exists: Ein Benutzer mit dieser E-Mail-Adresse existiert bereits.
</i18n>

<template>
  <div class="user-manager">
    <table class="user-table">
      <thead>
        <tr>
          <th>{{ $t('email') }}</th>
          <th>{{ $t('projects') }}</th>
          <th>{{ $t('devices') }}</th>
          <th>{{ $t('storage') }}</th>
          <th>{{ $t('user') }}</th>
          <th>{{ $t('creator') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="user in users"
          :key="user.id"
        >
          <td>{{ user.email }}</td>
          <td>
            <limit-select
              :value="user.limits.projects"
              :enabled="user.limits.projects !== undefined"
              @input="updateLimit(user.id, {projects: $event.value})"
            >
              {{ user.used.projects || 0 }}
            </limit-select>
          </td>
          <td>
            <limit-select
              :value="user.limits.devices"
              :enabled="user.limits.devices !== undefined"
              @input="updateLimit(user.id, {devices: $event.value})"
            >
              {{ user.used.devices || 0 }}
            </limit-select>
          </td>
          <td>
            <limit-select
              :value="user.limits.assets"
              :enabled="user.limits.assets !== undefined"
              @input="updateLimit(user.id, {assets: $event.value})"
            >
              {{ (user.used.assets || 0).toFixed(2) }} MB
            </limit-select>
          </td>
          <td>
            <limit-select
              :value="user.limits.users"
              :enabled="user.limits.users !== undefined"
              @input="updateLimit(user.id, {users: $event.value})"
            >
              {{ user.used.users || 0 }}
            </limit-select>
          </td>
          <td>
            {{ user.parent }}
          </td>
          <td>
            <settings
              size="s"
              @click="editUserMail(user.id)"
            />
            <user
              size="s"
              @click="editUserPassword(user.id)"
            />
            <delete
              size="s"
              color="danger"
              @click="deleteUser(user.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <tw-button
      @click="toggleNewUserPopup()"
      icon="add-circle"
      color="main"
    >
      {{ $t('add user') }}
    </tw-button>
  </div>
</template>

<script>
import axios from 'axios'
import Delete from '@/components/icons/Delete'
import Settings from '@/components/icons/Settings'
import User from '@/components/icons/User'
import LimitSelect from '@/components/ui/LimitSelect'
import TwButton from '@/components/ui/TwButton'
import { Prompt, Confirm, Error } from '@/plugins/snotify'

export default {
  name: 'UserManager',
  components: {
    Delete,
    Settings,
    TwButton,
    LimitSelect,
    User
  },
  data () {
    return {
      users: undefined
    }
  },
  created () {
    this.fetchUsers()
  },
  methods: {
    deleteUser (id) {
      const requestDelete = async () => {
        try {
          await axios.delete('users/' + id)
        } catch (e) {
          if (e.response.status === 403) {
            new Error(this.$t('couldnt delete user')).open()
          }
        }
        this.fetchUsers()
        this.$emit('dataChange')
      }
      new Confirm(this.$t('attention'), this.$t('confirm delete user'), requestDelete).open()
    },
    editUserMail (id) {
      const oldEmail = this.users.find(u => u.id === id).email
      const updateUserEmail = (email) => {
        this.updateUser(id, { email: email.toLowerCase() })
      }
      new Prompt(oldEmail, this.$t('enter new email'), updateUserEmail, oldEmail).open()
    },
    editUserPassword (id) {
      const updateUserPassword = (password) => {
        this.updateUser(id, { password })
      }
      new Prompt(this.$t('password'), this.$t('enter new password'), updateUserPassword).open()
    },
    async fetchUsers () {
      const response = await axios.get('/users')
      this.users = response.data
    },
    toggleNewUserPopup () {
      const newUser = {}
      const addPasswordAndCreate = (password) => {
        newUser.password = password
        this.createUser(newUser)
      }
      const addMail = (email) => {
        newUser.email = email.toLowerCase()
        new Prompt(this.$t('password'), this.$t('enter password'), addPasswordAndCreate).open()
      }
      new Prompt(this.$t('email'), this.$t('enter email'), addMail).open()
    },
    async createUser (user) {
      try {
        await axios.post('/users', user)
        this.fetchUsers()
        this.$emit('dataChange')
      } catch (e) {
        if (e.response.status === 402) {
          new Error(this.$t('user limit reached')).open()
        } else if (e.response.status === 409) {
          new Error(this.$t('user exists')).open()
        } else {
          throw e
        }
      }
    },
    async updateUser (id, user) {
      try {
        await axios.patch('/users/' + id, user)
        this.fetchUsers()
      } catch (e) {
        if (e.response.status === 409) {
          new Error(this.$t('user exists')).open()
        } else {
          throw e
        }
      }
    },
    updateLimit (id, limit) {
      const oldLimits = this.users.find(u => u.id === id).limits
      const limits = {
        ...oldLimits,
        ...limit
      }
      this.updateUser(id, { limits })
    }
  }
}
</script>
